/**
 * Kitten — frantic baby cat NPC (Babylon.js port).
 *
 * Starts frantic, calmed by Minna's Purr. Once calmed, follows Minna
 * and can be guided to the kitten pen.
 *
 * Visual: small capsule with orange/warm tint. Distress indicated by a
 * pulsing emissive glow and a point light that flickers.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3, Quaternion, Matrix } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import { NPC, NPCConfig } from "./NPC";
import { NPC_TUNING } from "../../config/GameConfig";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";
import { getAssetManager, type InstantiatedModel } from "../../systems/AssetManager";
import type { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import type { Light } from "@babylonjs/core/Lights/light";
import type { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import { tagAs } from "../../levels/PlaceholderTag";
import { setMeshVisibility } from "../../core/meshVisibility";
import { KittenGait } from "./KittenGait";

export interface KittenVisualOptions {
  /** Level-local visual scale; keep accepted levels' existing default. */
  modelScale?: number;
  /** Source front-axis correction, applied only to the walking shell. */
  walkingYawOffset?: number;
  /** Source-authored skeleton/clips; opt in per level. */
  walkingAnimation?: boolean;
  /** Ordinary kittens can communicate distress through behavior and the calm meter. */
  distressGlow?: boolean;
  shadowGenerator?: ShadowGenerator;
}

export class Kitten extends NPC {
  override get species(): string { return "kitten"; }

  private distressLight: PointLight;
  private _isPenned = false;
  private carrierSlot: number | null = null;
  private visibilityBeforeCarry = 1;
  private carriedModel: InstantiatedModel | null = null;
  private carriedLoading = false;
  private readonly importedRotations = new Map<TransformNode, Quaternion>();
  private carrierLight: Light | null = null;
  private lightExclusions: AbstractMesh[] = [];
  private shadowCasters: AbstractMesh[] = [];
  private gait: KittenGait | null = null;
  private previousX: number | null = null;

  constructor(scene: Scene, key: string, position: Vector3, private readonly visuals: KittenVisualOptions = {}) {
    const config: NPCConfig = {
      key,
      position,
      height: 0.6,
      radius: 0.2,
      color: '#ffaa66',
      behavior: {
        initialState: 'frantic',
        calmRadius: NPC_TUNING.CALM_RADIUS,
        calmThreshold: NPC_TUNING.KITTEN.CALM_THRESHOLD,
        calmRate: NPC_TUNING.KITTEN.CALM_RATE,
        calmDecay: NPC_TUNING.KITTEN.CALM_DECAY,
        followSpeed: NPC_TUNING.KITTEN.FOLLOW_SPEED,
        followDistance: NPC_TUNING.KITTEN.FOLLOW_DISTANCE,
      },
      modelPath: visuals.walkingAnimation ? "/assets/models/npcs/kitten_anim.glb" : "/assets/models/npcs/kitten.glb",
      modelScale: visuals.modelScale ?? 3.0,
    };

    super(scene, config);

    // Distress point light — flickers orange when frantic
    this.distressLight = new PointLight(`kitten_${key}_distress`, Vector3.Zero(), scene);
    this.distressLight.diffuse = new Color3(1.0, 0.4, 0.15);
    this.distressLight.specular = Color3.Black();
    this.distressLight.intensity = 1.5;
    this.distressLight.range = 3;
    this.distressLight.parent = this.mesh;
    this.distressLight.position = new Vector3(0, 0.5, 0);
    if (visuals.distressGlow === false) this.distressLight.setEnabled(false);
  }

  override update(time: number, deltaSeconds: number): void {
    const speed = this.previousX === null || deltaSeconds <= 0 ? 0
      : Math.abs(this.mesh.position.x - this.previousX) / deltaSeconds;
    this.previousX = this.mesh.position.x;
    if (this._isPenned) this.gait?.settle();
    else if (this.carrierSlot === null) this.gait?.update(speed > 8 ? 0 : speed, deltaSeconds);
    // Distress light only when frantic
    this.distressLight.setEnabled(this.visuals.distressGlow !== false && this.behavior.state === 'frantic');

    if (this.behavior.state === 'frantic') {
      // Flicker intensity
      this.distressLight.intensity = 1.2 + Math.sin(time * 8) * 0.5;
    }

    // Tint changes
    switch (this.behavior.state) {
      case 'frantic':
        this.material.diffuseColor = Color3.FromHexString('#ffaa66');
        this.material.emissiveColor = new Color3(0.3, 0.15, 0.05);
        break;
      case 'calming':
        this.material.diffuseColor = Color3.FromHexString('#ddcc88');
        this.material.emissiveColor = new Color3(0.15, 0.2, 0.05);
        break;
      case 'calmed':
      case 'following':
        this.material.diffuseColor = Color3.FromHexString('#eeddaa');
        this.material.emissiveColor = Color3.Black();
        break;
    }
    if (this.visuals.distressGlow === false) this.material.emissiveColor = Color3.Black();
  }

  /** Mark this kitten as penned (inside the kitten pen). */
  pen(): void {
    this._isPenned = true;
    this.behavior.setState('calmed');
    // Penned kittens leave the regular update loop; retire transient visuals
    // now, including when checkpoint/seed replay pens them before a frame.
    this.update(0, 0);
    this.updateCalmBar();
    gameEvents.emit(GameEvents.KITTEN_PENNED, { npcKey: this.npcKey });
  }

  get isPenned(): boolean {
    return this._isPenned;
  }

  /** Switch only the visual shell. The carrier owns the logical NPC slots. */
  setCarrierSlot(slot: number | null): void {
    if (this.mesh.isDisposed()) return;
    if (slot !== null && this.carrierSlot === null) {
      this.visibilityBeforeCarry = this.mesh.visibility;
      const parentVisibility = (this.mesh.parent as AbstractMesh | null)?.visibility;
      this.syncCarryVisibility(parentVisibility ?? this.mesh.visibility);
    } else if (slot === null && this.carrierSlot !== null) {
      this.syncCarryVisibility(this.visibilityBeforeCarry);
    }
    this.carrierSlot = slot;
    this.previousX = null;
    this.gait?.setSuspended(slot !== null);
    if (slot !== null) this.updateCalmBar();
    this.applyCarrierPresentation();
    if (slot !== null && !this.carriedModel && !this.carriedLoading) {
      void this.loadCarriedModel();
    }
  }

  protected override onVisualModelLoaded(): void {
    if (this.visualModel) this.registerShadows(this.visualModel);
    if (this.visuals.walkingAnimation && this.visualModel) {
      this.gait = KittenGait.create(this.visualModel.animationGroups, this.visuals.modelScale ?? 3);
      this.gait?.setSuspended(this.carrierSlot !== null);
    }
    this.applyCarrierPresentation();
  }

  private registerShadows(model: InstantiatedModel): void {
    if (!this.visuals.shadowGenerator) return;
    for (const mesh of model.meshes) {
      if (mesh.getTotalVertices() <= 0) continue;
      mesh.receiveShadows = true;
      this.visuals.shadowGenerator.addShadowCaster(mesh, false);
      this.shadowCasters.push(mesh);
    }
  }

  private async loadCarriedModel(): Promise<void> {
    this.carriedLoading = true;
    try {
      const model = await getAssetManager().instantiateModel(this.scene,
        "/assets/models/npcs/kitten_carried.glb", undefined, { doNotInstantiate: true });
      if (this.mesh.isDisposed() || this.scene.isDisposed) { model.dispose(); return; }
      this.carriedModel = model;
      this.registerShadows(model);
      for (const root of model.rootNodes) {
        root.parent = this.mesh;
        root.metadata = { ...root.metadata, catsArtRole: "carried-kitten" };
        tagAs(root, "final");
      }
      for (const mesh of model.meshes) {
        mesh.isPickable = false; tagAs(mesh, "final");
        setMeshVisibility(mesh, this.mesh.visibility);
      }
      this.applyCarrierPresentation();
    } catch (error) {
      if (!this.mesh.isDisposed()) console.warn("[Kitten] Keeping carry shell fallback:", error);
    } finally { this.carriedLoading = false; }
  }

  private placeShell(root: TransformNode, carried: boolean): void {
    let imported = this.importedRotations.get(root);
    if (!imported) {
      imported = root.rotationQuaternion?.clone() ?? Quaternion.FromEulerVector(root.rotation);
      this.importedRotations.set(root, imported);
    }
    if (!carried) {
      root.position.setAll(0);
      root.rotationQuaternion = imported.multiply(Quaternion.RotationAxis(Vector3.Up(), this.visuals.walkingYawOffset ?? 0));
      return;
    }
    // The gameplay slots retain their original different heights. Place the
    // shells side by side inside the pouch, compensating only their visuals.
    const offset = new Vector3(this.carrierSlot === 0 ? -.16 : .16,
      .29 - this.mesh.position.y, -.32 - this.mesh.position.z);
    const inverseFacing = Matrix.RotationY(-this.mesh.rotation.y);
    Vector3.TransformCoordinatesToRef(offset, inverseFacing, root.position);
    root.rotationQuaternion = Quaternion.RotationAxis(Vector3.Up(), -this.mesh.rotation.y).multiply(imported);
  }

  private clearCarrierLight(): void {
    if (this.carrierLight) {
      this.carrierLight.excludedMeshes = this.carrierLight.excludedMeshes.filter(
        (mesh) => !this.lightExclusions.includes(mesh));
    }
    this.carrierLight = null; this.lightExclusions = [];
  }

  private syncCarryVisibility(value: number): void {
    this.syncVisualVisibility(value);
    for (const mesh of this.carriedModel?.meshes ?? []) setMeshVisibility(mesh, value);
  }

  private applyCarrierPresentation(): void {
    const carried = this.carrierSlot !== null;
    for (const root of this.visualModel?.rootNodes ?? []) {
      root.setEnabled(!carried || !this.carriedModel);
      this.placeShell(root, carried);
    }
    for (const root of this.carriedModel?.rootNodes ?? []) {
      root.setEnabled(carried);
      if (carried) this.placeShell(root, true);
    }
    this.mesh.isVisible = !this.visualModel && !(carried && this.carriedModel);
    this.clearCarrierLight();
    if (carried) {
      this.carrierLight = this.scene.getLightByName("minnaLight");
      this.lightExclusions = (this.carriedModel ?? this.visualModel)?.meshes.filter((m) => m.getTotalVertices() > 0) ?? [];
      this.carrierLight?.excludedMeshes.push(...this.lightExclusions);
    }
  }

  override dispose(): void {
    this.gait?.dispose(); this.gait = null;
    this.clearCarrierLight();
    for (const mesh of this.shadowCasters) this.visuals.shadowGenerator?.removeShadowCaster(mesh, false);
    this.shadowCasters = [];
    this.carriedModel?.dispose(); this.carriedModel = null;
    this.importedRotations.clear();
    this.distressLight.dispose();
    super.dispose();
  }
}
